import { RichTextEditor } from "@/components/RichTextEditor";
import {
  DEFAULT_TEXT_LABEL_FONT_SIZE,
  type CanvasTextNoteNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import { downloadAsFile } from "@/utils/markdownExport";
import {
  BaseEntityCardQuickChat,
  CanvasObjectChrome,
  CHROME_CLEARANCE,
  CHROME_T1,
  NOTE_DEFAULT_SIZE,
  NOTE_MIN_SIZE,
  relativeWhen,
} from "@cohesive/ui-components";
import { NodeResizer } from "@xyflow/react";
import type { LexicalEditor } from "lexical";
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { LATTICE, snapToGrid } from "../canvas-controls.config";
import { registerChromeCollider } from "../chromeCollision";
import { useCanvasCommentsOptional } from "../comments/CanvasCommentsContext";
import { placementForNodeComment } from "../comments/threadPlacement";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useCanvasPermissions } from "../hooks/useCanvasPermissions";
import { useQuantizedZoom } from "../hooks/useQuantizedZoom";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { clearDomSelectionInside } from "../utils/domSelection";
import { TextNoteFloatingToolbar } from "./TextNoteFloatingToolbar";
import { DEFAULT_NOTE_COLOR } from "./shared/CanvasColorPalettes";
import { CanvasConnectHandles } from "./shared/CanvasConnectHandles";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

// Float the toolbar / quick chat above the chrome head band. The band is
// screen-constant, so the offset is too:
//   8 (bridge) + 42 (toolbar) + 8 (gap) = 58, + 26 (band + gap) while the
// chrome band is mounted (zoom ≥ T1).
const toolbarTopOffset = (zoom: number) =>
  58 + (zoom >= CHROME_T1 ? CHROME_CLEARANCE : 0);

export const TextNoteNode = memo(function TextNoteNode({
  id,
  data,
  selected,
  dragging,
  width = NOTE_DEFAULT_SIZE,
  height = NOTE_DEFAULT_SIZE,
  positionAbsoluteX,
  positionAbsoluteY,
}: CanvasTextNoteNodeProps) {
  const canvas = useCanvasApi();
  const { setToolbarPopoverOpen } = useCanvasUi();
  const { canEdit } = canvas;
  const { dispatchProcess } = useCanvasProcess();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();
  const { canvasId } = useParams<{ canvasId: string }>();

  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setIsPopoverOpen(open);
      setToolbarPopoverOpen(open);
    },
    [setToolbarPopoverOpen],
  );
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  // isEditing: true  → overlay removed, body editor is live, nodrag on editor
  // isEditing: false → overlay on body, card drags like other nodes
  //
  // Starts true when the node mounts already selected (e.g. just placed).
  // Remote-synced notes mount unselected and stay non-editing until clicked.
  const [isEditing, setIsEditing] = useState(selected);
  const [isFocused, setIsFocused] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const editorRef = useRef<LexicalEditor | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  const wasSelectedOnMouseDownRef = useRef(false);
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);

  const shouldAutoFocusRef = useRef(selected && canEdit);
  useEffect(() => {
    if (!shouldAutoFocusRef.current) return;
    const rafId = requestAnimationFrame(() => {
      const rootEl = editorRef.current?.getRootElement();
      if (!rootEl) return;
      rootEl.focus({ preventScroll: true });
      if (
        document.activeElement === rootEl ||
        rootEl.contains(document.activeElement)
      ) {
        shouldAutoFocusRef.current = false;
      }
    });
    return () => cancelAnimationFrame(rafId);
  });

  // The body is contenteditable only for the edit session (RichTextEditor
  // syncs editor.setEditable from readOnly). Leaving edit mode drops any
  // range still anchored in the editor so WebKit never paints it.
  useEffect(() => {
    if (isEditing) return;
    clearDomSelectionInside(editorRef.current?.getRootElement());
  }, [isEditing]);

  const noteColor = data?.note_color || DEFAULT_NOTE_COLOR;
  const title = data?.title || "Note";

  const content = data?.content || "";
  const fontFamily =
    data?.font_family || "system-ui, -apple-system, sans-serif";
  const fontSize = data?.font_size || DEFAULT_TEXT_LABEL_FONT_SIZE;

  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Multi-select unmounts this toolbar without Radix onOpenChange(false).
  // Clear chat + popover latches on multi-select or deselect — otherwise
  // showChatInput / isPopoverOpen alone would remount the wrong UI when
  // returning to single-select (node stays selected during multi-select).
  useEffect(() => {
    if (isMultiSelectToolbarOpen) {
      setShowChatInput(false);
    }
    if (isMultiSelectToolbarOpen || !selected) {
      if (isPopoverOpen) {
        handlePopoverOpenChange(false);
      }
    }
  }, [
    isMultiSelectToolbarOpen,
    selected,
    isPopoverOpen,
    handlePopoverOpenChange,
  ]);

  // Zoom ONLY, in 5% steps (exact once the gesture settles) — pans never
  // re-render the note, and a pinch re-renders it once per step, not tick.
  const zoom = useQuantizedZoom();

  // Scroll contract (ruled 2026-08-13, revised same day): the scrollbar
  // is VISIBLE exactly when scrolling is ACTIVE — edit mode and read-only
  // viewing. Everywhere else (rest, hover, select, multi-select) the
  // wheel pans the canvas and the thumb hides. Select-mode scrolling was
  // trialed and rolled back: the drag overlay is the wheel target there,
  // so deltas had to be FORWARDED to the sibling scroller — which loses
  // native momentum physics (choppy) and, being a passive listener, lets
  // the event bubble on to rubber-band the app shell. Revisit only by
  // making the scroller the real wheel target. The gutter is always
  // reserved so text never reflows between states.
  //
  // The body is a scroller (`overflow-y: auto`) ONLY while scrollActive and
  // `overflow: hidden` at rest. In WebKit an overflowing `overflow: auto`
  // box anywhere inside the viewport promotes the whole canvas viewport to
  // a composited layer, which rasterises every node at device scale and
  // blurs text at any zoom but 1 — for as long as the note exists.
  const scrollActive = isEditing || isFocused || !canEdit;

  const handleStartEditing = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(true);
  }, [canEdit]);

  // See the longer comment in TextLabelNode.handleContentChange — Lexical's
  // OnChangePlugin fires on selection-only changes too, so we must dedupe
  // against the live `data?.content` to avoid no-op writes that destroy the
  // user's redo history every time they click into the note.
  const handleMarkdownChange = useCallback(
    (markdown: string) => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        const trimmed = markdown.trim();
        if (trimmed === (data?.content ?? "")) return;
        canvas.updateNodeData(id, {
          content: trimmed,
          updated_at: new Date().toISOString(),
        });
      }, 300);
    },
    [id, canvas, data?.content],
  );

  const handleEditorReady = useCallback((editor: LexicalEditor | null) => {
    editorRef.current = editor;
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const handleBlur = useCallback(() => {
    setIsEditing(false);
    setIsFocused(false);
  }, []);

  const handleColorChange = useCallback(
    (color: string) => {
      canvas.updateNodeData(id, { note_color: color });
    },
    [id, canvas],
  );

  const handleChatSubmit = useCallback(
    async (message: string) => {
      const flowSessionId = await dispatchProcess({
        nodeId: id,
        referencedEntities: [
          {
            entity_id: id,
            entity_type: "canvas_text_note",
            name: title || "Note",
            platform: "cohesive",
          },
        ],
        action: "chat",
        customPrompt: message,
      });

      setShowChatInput(false);

      if (flowSessionId && canvasId) {
        openCanvasFloatingSession(flowSessionId);
      }
    },
    [id, title, dispatchProcess, canvasId, openCanvasFloatingSession],
  );

  const commitTitle = useCallback(
    (value: string) => {
      const trimmed = value.trim() || "Note";
      canvas.updateNodeData(id, { title: trimmed });
    },
    [id, canvas],
  );

  const handleToggleEye = useCallback(() => {
    canvas.updateNodeData(id, { title_hidden: !data?.title_hidden });
  }, [id, canvas, data?.title_hidden]);

  // Collision-gated backing (ruling 2026-08-13): register the wrapper so
  // chromeCollision can toggle data-coc-collide-head/-foot on it.
  const colliderRef = useCallback(
    (el: HTMLDivElement | null) => registerChromeCollider(id, el),
    [id],
  );

  // Stable identity — the chrome is memoized; an inline arrow would defeat it.
  const handleQuickChat = useCallback(() => setShowChatInput(true), []);

  const { canComment } = useCanvasPermissions(canvasId);
  const comments = useCanvasCommentsOptional();
  // The chrome's button aims at the note, not at a point in it, so the pin
  // takes the corner — the same spot the node context menu gives it.
  const handleComment = useCallback(() => {
    comments?.placeDraftAt(
      placementForNodeComment(
        { id, x: positionAbsoluteX, y: positionAbsoluteY, width, height },
        { x: positionAbsoluteX, y: positionAbsoluteY },
      ),
    );
  }, [comments, height, id, positionAbsoluteX, positionAbsoluteY, width]);

  const handleFontFamilyChange = useCallback(
    (newFontFamily: string) => {
      canvas.updateNodeData(id, { font_family: newFontFamily });
    },
    [id, canvas],
  );

  const handleFontSizeChange = useCallback(
    (newFontSize: string) => {
      canvas.updateNodeData(id, { font_size: newFontSize });
    },
    [id, canvas],
  );

  const handleDownloadMarkdown = useCallback(() => {
    const filename = title || `note-${id}`;
    downloadAsFile(content, `${filename}.md`, "text/markdown");
    toast.success("Downloaded as Markdown");
  }, [id, title, content]);

  // Toolbar follows selection only — do not keep it mounted via isPopoverOpen /
  // isFocused after deselection (nested Format menus used that latch to stick).
  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  const showToolbar =
    canEdit &&
    !dragging &&
    !isMultiSelectToolbarOpen &&
    selected &&
    selectedNodeCount === 1;

  return (
    <div
      ref={colliderRef}
      className={cn(
        "relative",
        isSectionPreviewMember && "ring-2 ring-canvas-selection/60 rounded-lg",
      )}
      style={{ width, height }}
    >
      {selected && !isEditing && canEdit && (
        <NodeResizer
          minWidth={NOTE_MIN_SIZE}
          minHeight={NOTE_MIN_SIZE}
          keepAspectRatio={false}
          // Settle on the lattice. Resize deltas are pointer-quantized (16
          // steps RELATIVE to the starting size), so a legacy off-lattice
          // dimension keeps its residue forever without this — a note with
          // height 200 resizes to 200±16k, always 8px off the grid.
          onResizeEnd={(_evt, params) => {
            canvas.updateNodeDimensions(id, {
              width: Math.max(NOTE_MIN_SIZE, snapToGrid(params.width, LATTICE)),
              height: Math.max(
                NOTE_MIN_SIZE,
                snapToGrid(params.height, LATTICE),
              ),
            });
          }}
        />
      )}

      <CanvasConnectHandles />

      {/* Hover halo — full cover, HOVER-ONLY (CSS shows it only while the
          node wrapper carries data-hovered). The systemic rule: the
          bridge and the resize handles never coexist. Unselected, there
          are no handles, so the halo can blanket node + bands and the
          pointer never drops hover crossing the gaps. Selected, the
          chrome is frozen on by the selection state itself (no hover
          dependence), so no halo renders and every resize handle keeps
          its full native edge territory — exactly main's feel. */}
      {!selected && !dragging && (
        <div
          className="canvas-note-halo absolute -left-2 -right-2 -top-9 -bottom-9"
          style={{ zIndex: 0 }}
          aria-hidden="true"
        />
      )}

      {/* The chrome (kit): label + eye + verbs above, authorship + download
          below. Screen-constant via its own counter-scale; zoom regimes
          inside the kit. Single selection freezes it (active stays true);
          MULTI-select quiets it to the label — mirroring the toolbar,
          where the per-item bar yields to the group bar (ruling
          2026-08-13). Hovering a multi-selected member stays quiet too.
          Unselected, the kit follows the wrapper's data-hovered itself. */}
      <CanvasObjectChrome
        label={title}
        width={width}
        zoom={zoom}
        active={selected ? selectedNodeCount <= 1 : "hover"}
        selected={!!selected}
        labelHidden={!!data?.title_hidden}
        author={data?.author_name}
        {...relativeWhen(data?.updated_at ?? data?.created_at)}
        onLabelCommit={canEdit ? commitTitle : undefined}
        onToggleEye={canEdit ? handleToggleEye : undefined}
        onQuickChat={canEdit ? handleQuickChat : undefined}
        onComment={canComment && comments ? handleComment : undefined}
        onDownload={handleDownloadMarkdown}
      />

      {/* Quick chat — opened from the chrome, so it must NOT require
          selection (the chrome is reachable from hover alone). Width is a
          lattice multiple at 1:1 (24 cells). */}
      {showChatInput && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={width}
          topOffset={toolbarTopOffset(zoom)}
        >
          <div style={{ width: 384 }}>
            <BaseEntityCardQuickChat
              visible={true}
              onSubmit={handleChatSubmit}
              onClose={() => setShowChatInput(false)}
            />
          </div>
        </CanvasFloatingToolbarPortal>
      )}

      {showToolbar && !showChatInput && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={width}
          topOffset={toolbarTopOffset(zoom)}
        >
          {
            <TextNoteFloatingToolbar
              visible={true}
              noteColor={noteColor}
              fontFamily={fontFamily}
              fontSize={fontSize}
              onColorChange={handleColorChange}
              onFontFamilyChange={handleFontFamilyChange}
              onFontSizeChange={handleFontSizeChange}
              editor={editorRef.current}
              onPopoverOpenChange={handlePopoverOpenChange}
            />
          }
        </CanvasFloatingToolbarPortal>
      )}

      <div
        className={cn(
          "w-full h-full rounded-lg border",
          "shadow-md",
          "flex flex-col",
          (selected || isEditing) &&
            "ring-2 ring-canvas-selection/60 shadow-md",
        )}
        style={{
          backgroundColor: noteColor,
          borderColor: selected
            ? "hsl(var(--primary) / 0.6)"
            : "rgba(0, 0, 0, 0.1)",
          paddingTop: "12px",
          paddingLeft: "12px",
          // Right padding lives INSIDE the scroller (editor pr-2), not on
          // the card: card padding sat outside the scroll container, which
          // parked the scrollbar 12px into the content zone, over the
          // text. 2px card + 8px editor: the scrollbar hugs the edge and
          // the text keeps its clearance from the gutter.
          paddingRight: "2px",
          paddingBottom: "20px",
          // Override --muted so code blocks (bg-muted) always contrast with the
          // note's own background color, independent of the app's light/dark theme.
          ["--muted" as string]: "0 0% 85%",
        }}
      >
        {!isEditing && canEdit && (
          <div
            className={cn(
              "absolute inset-0 z-10 rounded",
              "cursor-grab active:cursor-grabbing",
            )}
            onPointerDown={(e) => {
              wasSelectedOnMouseDownRef.current = selected ?? false;
              mouseDownPosRef.current = { x: e.clientX, y: e.clientY };
              if (selected) {
                e.stopPropagation();
              }
            }}
            onPointerUp={(e) => {
              if (!mouseDownPosRef.current) {
                return;
              }

              const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
              const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
              const moved = dx > 3 || dy > 3;
              mouseDownPosRef.current = null;

              if (moved || dragging) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }

              // First click selects (mousedown was not selected); second click+release
              // without drag enters edit. Require still selected at up so a gesture that
              // only selected on mouseup does not open the editor on the same click.
              if (!wasSelectedOnMouseDownRef.current || !selectedRef.current) {
                return;
              }

              handleStartEditing();
            }}
          />
        )}

        <div
          ref={contentWrapperRef}
          className={cn(
            "flex-1 min-h-0 relative flex flex-col overflow-hidden",
            isEditing && "cursor-text",
            // View-only: let wheel scroll note content instead of panning the board.
            !canEdit && "nowheel",
          )}
          style={{ fontFamily, fontSize }}
        >
          <RichTextEditor
            value={content}
            onChange={handleMarkdownChange}
            namespace={`TextNote-${id}`}
            showToolbar={false}
            enableSkills={false}
            enableEntities={false}
            enablePlaceholders={false}
            autoFocus={canEdit && isEditing}
            readOnly={!canEdit || !isEditing}
            className="border-0 shadow-none rounded-none overflow-hidden bg-transparent flex-1 min-h-0 flex flex-col h-full"
            editorClassName={cn(
              "canvas-note-scroll w-full h-full min-h-0 max-h-none !min-h-0 outline-none py-0 !py-0 pl-0 !pl-0 pr-2 !pr-2 text-sticky-foreground",
              isEditing && "nodrag",
              scrollActive
                ? "overflow-y-auto nowheel canvas-note-scroll--active"
                : "overflow-hidden",
            )}
            placeholder={isEditing ? "Type your note here..." : "\u200b"}
            placeholderClassName={cn(
              // Same face as the note body (inherits font_family), muted ink,
              // no italics — DS placeholder register.
              "py-0 !py-0 pl-0 !pl-0 pr-2 !pr-2",
              "text-sticky-foreground/50",
              !isEditing && "opacity-0",
            )}
            controlledValueSync={!isEditing}
            onEditorReady={handleEditorReady}
            onEditorFocus={() => {
              if (!canEdit) return;
              setIsEditing(true);
              setIsFocused(true);
            }}
            onEditorBlur={() => {
              if (!canEdit) return;
              setIsFocused(false);
              handleBlur();
            }}
            onEditorKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key === "Tab") e.preventDefault();
              e.stopPropagation();
              if (e.key === "Escape") {
                const rfRoot = (e.target as HTMLElement).closest<HTMLElement>(
                  ".react-flow",
                );
                if (rfRoot) {
                  rfRoot.focus({ preventScroll: true });
                } else {
                  (e.target as HTMLElement).blur();
                }
              }
            }}
          />
        </div>
      </div>
    </div>
  );
});
